'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { carregarContextos, selectContextosNaoAbertos, trocarContexto } from '@/features/contextoAcesso/store';
import { rotaDoContexto } from '@/features/contextoAcesso/utils/rotaDoContexto';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ContextoAcessoParte } from '@/types/contextoAcesso/types';

/**
 * Aviso de processo em que a pessoa é parte e que ela ainda não abriu.
 *
 * Fechar aqui é só desta sessão: nada é persistido, então o aviso volta no
 * próximo login. Ele só some de verdade quando ela ABRE o processo — decisão de
 * produto, porque o risco é prazo correndo, não navegação ruim.
 */
export const AvisoProcessoNovo: React.FC = () => {
  const $ = _c(24);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const naoAbertos = useAppSelector(selectContextosNaoAbertos);
  const [ocultoNestaSessao, setOcultoNestaSessao] = useState(false);
  let t0;
  let t1;
  if ($[0] !== dispatch) {
    t0 = () => {
      dispatch(carregarContextos());
    };
    t1 = [dispatch];
    $[0] = dispatch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== dispatch || $[4] !== router) {
    t2 = async contexto => {
      const resultado = await dispatch(trocarContexto({
        tipo: contexto.tipo,
        id: contexto.id
      }));
      if (trocarContexto.fulfilled.match(resultado)) {
        router.push(rotaDoContexto(contexto));
      }
    };
    $[3] = dispatch;
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const abrir = t2;
  if (ocultoNestaSessao || naoAbertos.length === 0) {
    return null;
  }
  const primeiro = naoAbertos[0];
  const restantes = naoAbertos.length - 1;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AlertTriangle className="text-warning-600 mt-0.5 shrink-0" size={20} />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = naoAbertos.length === 1 ? "Voc\xEA foi notificado em um processo" : `Você foi notificado em ${naoAbertos.length} processos`;
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-sm font-semibold text-neutral-900">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = primeiro.numeroIdentificador ? ` · ${primeiro.numeroIdentificador}` : "";
  const t7 = restantes > 0 ? ` e mais ${restantes}` : "";
  let t8;
  if ($[9] !== primeiro.nome || $[10] !== t6 || $[11] !== t7) {
    t8 = <p className="mt-0.5 text-sm text-neutral-600">{primeiro.nome}{t6}{t7}. Há prazo correndo.</p>;
    $[9] = primeiro.nome;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== abrir || $[14] !== primeiro) {
    t9 = <button type="button" onClick={() => abrir(primeiro)} className="text-brand-700 mt-2 text-sm font-medium underline underline-offset-4">Abrir o processo</button>;
    $[13] = abrir;
    $[14] = primeiro;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t5 || $[17] !== t8 || $[18] !== t9) {
    t10 = <div className="min-w-0 flex-1">{t5}{t8}{t9}</div>;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setOcultoNestaSessao(true);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <button type="button" onClick={t11} aria-label="Fechar aviso" className="shrink-0 text-neutral-400 hover:text-neutral-600"><X size={18} /></button>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10) {
    t13 = <div className="border-warning-500/30 bg-warning-500/10 flex items-start gap-3 rounded-[16px] border px-4 py-3">{t3}{t10}{t12}</div>;
    $[22] = t10;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};